import React, { ReactNode } from 'react';
import {
  InputPrefix,
  InputSuffix,
  InputStartSlot,
  InputEndSlot,
  InputStartIcon,
  InputEndIcon,
  InputStartSelect,
  InputEndSelect,
} from '../__subcomponents';
import { hasButtonOrIconButton } from '../__utils';

/**
 * Typed object returned by useInputChildren hook.
 * Contains all detected Input subcomponent children and metadata about them.
 * Used by Input.tsx to conditionally render children and apply styling.
 */
export interface InputChildrenData {
  // Slot children (render generic custom content)
  startSlotChildren: React.ReactElement | undefined;
  endSlotChildren: React.ReactElement | undefined;

  // Metadata: whether slots contain interactive buttons
  // Used to adjust padding and apply data-has-*-button attributes for CSS selectors
  hasStartSlotButton: boolean;
  hasEndSlotButton: boolean;

  // Prefix/Suffix children (render text-only labels like "$" or "%")
  prefixChildren: React.ReactElement | undefined;
  suffixChildren: React.ReactElement | undefined;

  // Select children (render dropdown selectors)
  startSelectChildren: React.ReactElement | undefined;
  endSelectChildren: React.ReactElement | undefined;

  // Icon children (render single icons)
  startIconChildren: React.ReactElement | undefined;
  endIconChildren: React.ReactElement | undefined;
}

/**
 * Extracts and type-checks all Input subcomponent children from the children prop.
 *
 * This hook searches through all children to find specific Input subcomponent types
 * (InputStartSlot, InputPrefix, InputStartIcon, etc.) and returns them with proper typing.
 * It also detects if slots contain interactive elements (Button, IconButton) which
 * affects padding and layout through CSS data attributes.
 *
 * Why a hook? This logic is isolated and testable, making it easier to:
 * - Understand child detection logic in one place
 * - Test filtering and type detection independently
 * - Reuse in other Input variants
 *
 * @param children - Input component's children prop
 * @returns Typed object with all detected children and metadata
 */
export const useInputChildren = (children: ReactNode): InputChildrenData => {
  // Convert children to array for easier searching
  // Handles single child, arrays, fragments, null, and undefined
  const childrenArray = React.Children.toArray(children);

  // Search for each Input subcomponent type
  // Detects by component type reference for type safety
  const startSlotChildren = childrenArray.find(
    (child) => React.isValidElement(child) && child.type === InputStartSlot,
  ) as React.ReactElement | undefined;

  const endSlotChildren = childrenArray.find(
    (child) => React.isValidElement(child) && child.type === InputEndSlot,
  ) as React.ReactElement | undefined;

  // Detect if slots contain interactive buttons (changes padding/layout)
  const hasStartSlotButton = hasButtonOrIconButton(startSlotChildren);
  const hasEndSlotButton = hasButtonOrIconButton(endSlotChildren);

  // Prefix/suffix are text-only elements like "$" or ".00"
  const prefixChildren = childrenArray.find(
    (child) => React.isValidElement(child) && child.type === InputPrefix,
  ) as React.ReactElement | undefined;

  const suffixChildren = childrenArray.find(
    (child) => React.isValidElement(child) && child.type === InputSuffix,
  ) as React.ReactElement | undefined;

  // Selects are dropdown elements (for currency, country, etc.)
  const startSelectChildren = childrenArray.find(
    (child) => React.isValidElement(child) && child.type === InputStartSelect,
  ) as React.ReactElement | undefined;

  const endSelectChildren = childrenArray.find(
    (child) => React.isValidElement(child) && child.type === InputEndSelect,
  ) as React.ReactElement | undefined;

  // Icons are single icon elements
  const startIconChildren = childrenArray.find(
    (child) => React.isValidElement(child) && child.type === InputStartIcon,
  ) as React.ReactElement | undefined;

  const endIconChildren = childrenArray.find(
    (child) => React.isValidElement(child) && child.type === InputEndIcon,
  ) as React.ReactElement | undefined;

  // Return all detected children for use in Input rendering logic
  return {
    startSlotChildren,
    endSlotChildren,
    hasStartSlotButton,
    hasEndSlotButton,
    prefixChildren,
    suffixChildren,
    startSelectChildren,
    endSelectChildren,
    startIconChildren,
    endIconChildren,
  };
};
